// =====================================================================
// map-core/render.js — the single, framework-agnostic map renderer shared by
// the editor (React shell), the export, and the embed (vanilla shell).
//
// It produces a STRUCTURED SCENE (validated colors only). Each shell renders it
// its own safe way:
//   • editor  -> buildMapSvg() string injected into a same-origin <svg>
//   • embed   -> iterates buildScene() and builds DOM nodes (no innerHTML, per
//                SECURITY_RULES — only validated hex colors reach attributes)
//
// Phase 2 (Growth) — county drill-down, implemented here:
//   • LEVEL-AGNOSTIC: buildScene takes its geometry as input (a { paths,
//     labelPoints, codes } set), so county geometry (countyGeometry(bundle),
//     lazy-loaded per state) renders through the same path. See countyGeometry()
//     and zoomViewBox() below.
//   • MIXED-STATE HATCH: a state edited county-by-county can't be one solid
//     color at national zoom, so it renders as a neutral diagonal hatch via a
//     shared <pattern> (mixedCodes -> HATCH_REF). One place, both shells.
//     (This replaces the earlier "stripe multiple distributors per state" idea —
//     splitting at the county level is the product decision; see
//     PHASE2_COUNTY_SPEC.md.)
// =====================================================================

import { VIEWBOX, STATE_PATHS, STATE_LABEL_POINTS } from "./geometry.js";
import { CALLOUT_STATES, CALLOUT, LABEL_OVERRIDES } from "./config.js";

export { VIEWBOX };

export const abbr = (code) => code.slice(3); // "US-CA" -> "CA"

export const STATE_GEOMETRY = {
  paths: STATE_PATHS,
  labelPoints: STATE_LABEL_POINTS,
  codes: Object.keys(STATE_PATHS),
};

const CALLOUT_SET = new Set(CALLOUT_STATES);
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function hex(c, fallback) {
  return typeof c === "string" && HEX_RE.test(c) ? c : fallback;
}

// Legible ink (black/white) for text on a given fill.
export function pickInk(fillHex) {
  const c = hex(fillHex, "#cccccc");
  const r = parseInt(c.slice(1, 3), 16);
  const g = parseInt(c.slice(3, 5), 16);
  const b = parseInt(c.slice(5, 7), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#1f2937" : "#ffffff";
}

// Resolve map_regions + distributors into fills. Returns Map<code, string[]>.
export function resolveFills(regions, distributorsByName, level = "state") {
  const out = new Map();
  for (const r of regions || []) {
    if (r.level !== level || r.active === false || !r.distributor) continue;
    const d = distributorsByName.get(r.distributor);
    if (!d || !hex(d.color_hex, null)) continue;
    const list = out.get(r.region_code) || [];
    list.push(d.color_hex);
    out.set(r.region_code, list);
  }
  return out;
}

// Primary fill for a region. A region maps to a LIST of distributor colors; the
// solid fill is the first. >1 distributor in a single region is no longer
// expressed by striping the region — instead the region is split at the county
// level and the parent state renders the neutral "mixed" hatch (see below).
function primaryFill(fills, code, unassigned) {
  const list = fills && fills.get(code);
  return hex(list && list[0], unassigned);
}

// ---- by-county "mixed" hatch ------------------------------------------------
// A state edited county-by-county can't be one solid color at the national
// zoom, so it renders as a neutral diagonal hatch meaning "click to drill in".
// One shared <pattern> both shells emit into <defs> and reference by url().
export const HATCH_PATTERN_ID = "bcm-hatch";
const HATCH_STROKE = "#9ca3af"; // neutral gray-400 — intentionally NOT a brand color
const HATCH_REF = `url(#${HATCH_PATTERN_ID})`;

function hatchDef(baseHex) {
  return { id: HATCH_PATTERN_ID, base: hex(baseHex, "#e5e7eb"), stroke: HATCH_STROKE };
}

// Build a structured, render-ready scene. All colors are validated hex.
// State view: defaults. County drill view: pass geometry=countyGeometry(bundle),
// useCallouts:false, and (usually) showLabels:false (names show on hover).
export function buildScene({
  fills,
  unassignedColor = "#e5e7eb",
  labelColor = "#1f2937",
  showLabels = true,
  geometry = STATE_GEOMETRY,
  mixedCodes = null,    // Set<code> of states in by-county mode -> render as hatch
  useCallouts = true,   // state-only Northeast callout column
  labelText = abbr,     // how a code becomes its label ("US-CA" -> "CA")
} = {}) {
  const { paths, labelPoints, codes } = geometry;
  const unassigned = hex(unassignedColor, "#e5e7eb");
  const lbl = hex(labelColor, "#1f2937");
  const mixed = mixedCodes instanceof Set ? mixedCodes : null;
  let usedHatch = false;

  const shapes = [];
  for (const code of codes) {
    const d = paths[code];
    if (!d) continue;
    if (mixed && mixed.has(code)) {
      shapes.push({ code, d, fill: HATCH_REF, mixed: true });
      usedHatch = true;
    } else {
      shapes.push({ code, d, fill: primaryFill(fills, code, unassigned) });
    }
  }

  const labels = [];
  const callouts = [];
  if (showLabels) {
    for (const code of codes) {
      if (useCallouts && CALLOUT_SET.has(code)) continue;
      const p = LABEL_OVERRIDES[code] || labelPoints[code];
      if (!p) continue;
      labels.push({ code, x: p[0], y: p[1], text: labelText(code), color: lbl });
    }
    if (useCallouts) {
      CALLOUT_STATES.forEach((code, i) => {
        const y = CALLOUT.top + i * (CALLOUT.height + CALLOUT.gap);
        const isMixed = mixed && mixed.has(code);
        const fill = isMixed ? HATCH_REF : primaryFill(fills, code, unassigned);
        if (isMixed) usedHatch = true;
        callouts.push({
          code, x: CALLOUT.x, y, w: CALLOUT.width, h: CALLOUT.height,
          fill, ink: pickInk(isMixed ? unassigned : fill), text: abbr(code),
        });
      });
    }
  }

  const defs = usedHatch ? [hatchDef(unassignedColor)] : [];
  return { viewBox: VIEWBOX, shapes, labels, callouts, defs };
}

// ---- county drill helpers ---------------------------------------------------

// Convert a loaded county bundle (counties/<stateFips>.json) into the geometry
// shape buildScene expects. Keys are 5-digit county FIPS (== map_regions.region_code).
export function countyGeometry(bundle) {
  const paths = {}, labelPoints = {}, names = {};
  const counties = (bundle && bundle.counties) || {};
  for (const fips of Object.keys(counties)) {
    const c = counties[fips];
    if (!c || !c.d) continue;
    paths[fips] = c.d;
    if (c.lp) labelPoints[fips] = c.lp;
    if (c.name) names[fips] = c.name;
  }
  return { paths, labelPoints, codes: Object.keys(paths), names };
}

// Padded viewBox string for zooming into a state's bbox [x,y,w,h]. The shells
// set this as the <svg viewBox> to "enlarge" a state; county paths are already
// in national coordinates so they register without any re-projection.
export function zoomViewBox(bbox, pad = 0.06) {
  if (!Array.isArray(bbox) || bbox.length !== 4) return VIEWBOX;
  let [x, y, w, h] = bbox;
  if (![x, y, w, h].every(Number.isFinite) || w <= 0 || h <= 0) return VIEWBOX;
  const px = w * pad, py = h * pad;
  x -= px; y -= py; w += px * 2; h += py * 2;
  return `${+x.toFixed(1)} ${+y.toFixed(1)} ${+w.toFixed(1)} ${+h.toFixed(1)}`;
}

// ---- SVG serialization ------------------------------------------------------

// Emit a <pattern> for the by-county hatch. base/stroke are validated hex.
function hatchSvg(p) {
  return `<pattern id="${p.id}" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">` +
    `<rect width="6" height="6" fill="${p.base}"></rect>` +
    `<line x1="0" y1="0" x2="0" y2="6" stroke="${p.stroke}" stroke-width="2"></line>` +
    `</pattern>`;
}

// Serialize a scene to inner-SVG markup (for the same-origin editor/export).
export function buildMapSvg(opts) {
  const s = buildScene(opts);
  let svg = "";
  if (s.defs && s.defs.length) {
    svg += "<defs>";
    for (const p of s.defs) svg += hatchSvg(p);
    svg += "</defs>";
  }
  for (const sh of s.shapes) {
    svg += `<path class="bcm-state" data-code="${sh.code}" d="${sh.d}" fill="${sh.fill}"></path>`;
  }
  for (const l of s.labels) {
    svg += `<text class="bcm-label" x="${l.x}" y="${l.y}" text-anchor="middle" dominant-baseline="central" fill="${l.color}" font-size="12" font-weight="600" font-family="Inter, system-ui, sans-serif">${l.text}</text>`;
  }
  for (const c of s.callouts) {
    svg += `<g class="bcm-callout" data-code="${c.code}">` +
      `<rect x="${c.x}" y="${c.y}" width="${c.w}" height="${c.h}" rx="3" fill="${c.fill}" stroke="#ffffff" stroke-width="1"></rect>` +
      `<text x="${c.x + c.w / 2}" y="${c.y + c.h / 2}" text-anchor="middle" dominant-baseline="central" fill="${c.ink}" font-size="12" font-weight="700" font-family="Inter, system-ui, sans-serif">${c.text}</text>` +
      `</g>`;
  }
  return svg;
}
